import type { DateTime } from "luxon"

import {
  MIN_ZOOM_MINUTES,
  MIN_ZOOM_PADDING_MINUTES,
  ZOOM_PADDING_PERCENTAGE,
} from "helpers/utils/constants"

/**
 * Turns a raw selection (two arbitrary instants) into the date range to apply:
 * orders the bounds, guarantees a minimum span, and adds breathing room around
 * it. Shared by both zoom gestures — mouse drag-select and two-finger span.
 */
export const computeZoomRange = (
  a: DateTime,
  b: DateTime,
): { start: DateTime; end: DateTime } => {
  const [rawStart, rawEnd] = a < b ? [a, b] : [b, a]

  // Guarantee MIN_ZOOM_MINUTES by expanding around the midpoint, so a tiny
  // selection still yields a queryable window.
  const duration = rawEnd.diff(rawStart, "minutes").minutes
  const [start, end] =
    duration < MIN_ZOOM_MINUTES
      ? (() => {
          const midPoint = rawStart.plus({ minutes: duration / 2 })
          return [
            midPoint.minus({ minutes: MIN_ZOOM_MINUTES / 2 }),
            midPoint.plus({ minutes: MIN_ZOOM_MINUTES / 2 }),
          ]
        })()
      : [rawStart, rawEnd]

  // Proportional padding so the selected data does not sit flush against the
  // plot edges after the zoom.
  const rangeDuration = end.diff(start, "minutes").minutes
  const paddingMinutes = Math.max(
    MIN_ZOOM_PADDING_MINUTES,
    rangeDuration * ZOOM_PADDING_PERCENTAGE,
  )

  return {
    start: start.minus({ minutes: paddingMinutes }),
    end: end.plus({ minutes: paddingMinutes }),
  }
}

/** The horizontal slice of the plot: all the x <-> time mapping needs. */
interface XBounds {
  left: number
  width: number
}

/** The full plot rectangle, for the hit tests. */
export interface PlotBounds extends XBounds {
  top: number
  height: number
}

/** A position in the SVG's own coordinates. */
export interface SvgPoint {
  x: number
  y: number
}

/** A time range, in epoch milliseconds. */
export interface TimeWindow {
  min: number
  max: number
}

/** The selection band, in pixels, as the overlay draws it. */
export interface SelectionBand {
  x: number
  width: number
}

/** True while the point sits inside the plot, edges included. */
export const isInsideDrawingArea = (point: SvgPoint, drawingArea: PlotBounds): boolean =>
  point.x >= drawingArea.left &&
  point.x <= drawingArea.left + drawingArea.width &&
  point.y >= drawingArea.top &&
  point.y <= drawingArea.top + drawingArea.height

/** True once the pointer has left the plot above or below it. */
export const isOutsideVertically = (
  y: number,
  drawingArea: { top: number; height: number },
): boolean => y < drawingArea.top || y > drawingArea.top + drawingArea.height

/** Pins an x coordinate to the plot, so a pointer past the edge reads as the edge. */
export const clampToPlotX = (x: number, drawingArea: XBounds): number =>
  Math.max(drawingArea.left, Math.min(x, drawingArea.left + drawingArea.width))

// Unguarded x -> time. Every caller checks the domain before getting here.
const toTimestamp = (
  x: number,
  drawingArea: XBounds,
  domainMin: number,
  timeRange: number,
) => domainMin + ((x - drawingArea.left) / drawingArea.width) * timeRange

interface PixelParams {
  drawingArea: XBounds
  domainMin: number
  domainMax: number
}

/**
 * Maps an x coordinate to the instant it points at, or null if the domain
 * cannot be divided. The coordinate is taken as given: clamp it first with
 * `clampToPlotX` when it may come from outside the plot.
 */
export const pixelToTimestamp = ({
  x,
  drawingArea,
  domainMin,
  domainMax,
}: PixelParams & { x: number }): number | null => {
  const timeRange = domainMax - domainMin
  if (timeRange === 0 || !isFinite(timeRange)) return null

  return toTimestamp(x, drawingArea, domainMin, timeRange)
}

/**
 * Places a time window back on the plot, as the x and width of the selection
 * band that previews it.
 */
export const windowToPixels = ({
  window,
  drawingArea,
  domainMin,
  domainMax,
}: PixelParams & { window: TimeWindow }): SelectionBand | null => {
  const timeRange = domainMax - domainMin
  if (timeRange <= 0 || !isFinite(timeRange)) return null

  return {
    x: drawingArea.left + ((window.min - domainMin) / timeRange) * drawingArea.width,
    width: ((window.max - window.min) / timeRange) * drawingArea.width,
  }
}

interface PixelSpanParams extends PixelParams {
  /** The two x coordinates bounding the selection, in SVG pixels, any order. */
  xA: number
  xB: number
}

/**
 * Maps the horizontal span between two points to the time window it covers.
 *
 * This is what a two-finger gesture selects: the fingers are the edges of the
 * range, so the window grows as they separate. Coordinates are clamped to the
 * drawing area first, which is what lets a finger sit slightly off the plot
 * without distorting the selection.
 */
export const pixelSpanToWindow = ({
  xA,
  xB,
  drawingArea,
  domainMin,
  domainMax,
}: PixelSpanParams): TimeWindow | null => {
  const timeRange = domainMax - domainMin
  if (!isFinite(timeRange) || timeRange <= 0) return null
  if (drawingArea.width <= 0) return null

  const toTime = (x: number) =>
    toTimestamp(clampToPlotX(x, drawingArea), drawingArea, domainMin, timeRange)

  const [min, max] = [toTime(xA), toTime(xB)].sort((l, r) => l - r)
  return { min, max }
}
